import React, { useMemo, useState } from 'react';

import { useStyles } from './style';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { ToastService } from '../../../../shared/services/toastService';
import { usePurchaseUnlimitedSupplierMutation } from '../../../../api/user';
import clsx from 'clsx';
import { colorVariables } from '../../../../styles/colorVariables';
import { Box } from '@material-ui/core';
import { ThemedButton } from '../../../../shared/components/themed-button';
import { ButtonLoader } from '../../../../shared/components/button-loader';
import { useAddPaymentMethodMutation } from '../../../../api/payment-method';
import { PlanTypes } from '../CurrentPlan';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';

interface Props {
  isYearly?: boolean;
  newPlan?: PlanTypes
  showActivatedPopup?: () => void;
  buttonText?: string;
  isCreatePayment?: boolean;
  completeCreatePayment?: () => void;
}

const baseStyles = {
  fontSize: '16px',
  color: colorVariables.navy,
  fontWeight: '400',
  fontFamily: 'Open Sans, sans-serif',
  backgroundColor: colorVariables.white,
  '::placeholder': {
    color: colorVariables.grey60,
    fontWeight: '400',
    fontFamily: 'Open Sans, sans-serif',
    fontSize: '16px',
  },
};

export const PaymentCards: React.FC<Props> = ({ newPlan, isYearly = true, showActivatedPopup, buttonText, isCreatePayment, completeCreatePayment }) => {
  const classes = useStyles();
  const { isMobile } = useScreenSize();
  const [cardError, setCardError] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();
  const [purchaseUnlimitedSupplier] = usePurchaseUnlimitedSupplierMutation();
  const [addPaymentMethod] = useAddPaymentMethodMutation();
  const [disabledBtn, setDisabledBtn] = useState(false);

  const handleCardChange = (event: any) => {
    if (event.error) {
      setCardError(event.error.message);
    } else {
      setCardError(null);
    }
  };

  const handleSuccessPayment = () => {
    showActivatedPopup && showActivatedPopup();
  };

  const confirmCardPayment = async (secret?: string | null) => {
    if (!secret) {
      handleSuccessPayment();
      return;
    }
    const paymentIntentRes = await stripe?.retrievePaymentIntent(secret as string);
    if (paymentIntentRes?.paymentIntent?.status !== 'requires_confirmation') {
      handleSuccessPayment();
      return;
    }

    const confirmPayment = await stripe?.confirmCardPayment(secret);
    if (confirmPayment?.error) {
      ToastService.error(confirmPayment.error.message || 'Payment failed');
    } else {
      handleSuccessPayment();
    }
  };

  const confirmActivateSubscription = async () => {
    if (!stripe || !elements) {
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    if (cardError || !cardNumber) {
      ToastService.error('Invalid payment method');
      return;
    }

    setDisabledBtn(true);
    const { paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber
    });
    if (paymentMethod) {

      if(isCreatePayment) {
        addPaymentMethod({
          payment_method: paymentMethod.id,
        }).then((_) => {
          setDisabledBtn(false);
          ToastService.success('Payment method added successfully');
          completeCreatePayment && completeCreatePayment();
        });

        return;
      }

      purchaseUnlimitedSupplier({
        payment_method: paymentMethod.id,
        payment_type: newPlan as string,
        payment_interval: isYearly ? 'year' : 'month',
      }).then((res) => {
        if ('data' in res && res.data?.success) {
          confirmCardPayment(res.data?.client_secret);
        } else {
          setDisabledBtn(false);
        }
      });
    } else {
      ToastService.error('Invalid payment method');
      setDisabledBtn(false);
    }
  };

  return (
    <div className={classes.cardForm}>
      <div>
        <div className={classes.label}>Card Number</div>
        <CardNumberElement
          onChange={handleCardChange}
          options={{
            showIcon: true,
            style: {
              base: baseStyles,
            },
            classes: {
              base: classes.baseClass,
              focus: classes.focus,
              invalid: classes.invalid,
            },
          }}
        />
      </div>
      <div className={classes.fieldWrap}>
        <div className={classes.smallField}>
          <div className={classes.label}>Expiry Date</div>
          <CardExpiryElement
            onChange={handleCardChange}
            options={{
              style: {
                base: baseStyles,
              },
              classes: {
                base: clsx(classes.baseClass, classes.baseClassExp),
                focus: classes.focus,
                invalid: classes.invalid,
              },
            }}
          />
        </div>
        <div className={classes.smallField}>
          <div className={classes.label}>CVC</div>
          <CardCvcElement
            onChange={handleCardChange}
            options={{
              style: {
                base: baseStyles,
              },
              classes: {
                base: clsx(classes.baseClass, classes.baseClassCvc),
                focus: classes.focus,
                invalid: classes.invalid,
              },
            }}
          />
        </div>
      </div>
      <Box className={classes.actionContainer}>
        <ThemedButton
          onClick={confirmActivateSubscription}
          title={buttonText || 'Confirm & Activate Subscription'}
          buttonStyle='blueDarkButton'
          width={isMobile ? 300 : 400}
          disabled={disabledBtn}
          endIcon={disabledBtn ? <ButtonLoader /> : null}
        />
      </Box>
    </div>
)};
